import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Form, Grid, Icon, Label, Loader, Progress, Table } from 'semantic-ui-react';
import styled from 'styled-components';
import moment from 'moment-timezone';
import { useHistory } from 'react-router-dom';
import Button from '../../../../components/Button';
import {
  YelumBatchImport,
  downloadYelumBatchImportReport,
  listYelumBatchImports
} from '../../../../services/yelum-batch-imports';
import { dangerNotification } from '../../../../services/notification';
import NewYelumImportModal from './components/NewYelumImportModal';
import { iRootState } from '../../../../store';

const PageContainer = styled.div`
  padding: 20px;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 20px;

  h1 {
    margin: 0;
  }
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const TableContainer = styled.div`
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  overflow-x: auto;
`;

const statusLabels: Record<string, { text: string; color: string }> = {
  DRAFT: { text: 'Revisão pendente', color: 'blue' },
  PENDING: { text: 'Pendente', color: 'yellow' },
  RUNNING: { text: 'Processando', color: 'yellow' },
  PROCESSING: { text: 'Processando', color: 'yellow' },
  SUCCESS: { text: 'Concluída', color: 'green' },
  COMPLETED: { text: 'Concluída', color: 'green' },
  COMPLETED_WITH_ERRORS: { text: 'Concluída com erros', color: 'orange' },
  FAILED: { text: 'Erro', color: 'red' },
  CANCELLED: { text: 'Descartada', color: 'grey' }
};

const getStatusLabel = (status?: string) =>
  statusLabels[status || ''] || { text: status || 'Desconhecido', color: 'grey' };

const getProgress = (item: YelumBatchImport): number => {
  if (typeof item.progress === 'number') {
    return item.progress <= 1 ? item.progress * 100 : item.progress;
  }
  if (item.totalItems && typeof item.processedItems === 'number') {
    return (item.processedItems / item.totalItems) * 100;
  }
  return ['SUCCESS', 'COMPLETED', 'COMPLETED_WITH_ERRORS'].includes(item.status)
    ? 100
    : 0;
};

const isPollingStatus = (status?: string) =>
  ['PENDING', 'RUNNING', 'PROCESSING'].includes(status || '');

const YelumImportsPage: React.FC = () => {
  const history = useHistory();
  const estateOptions = useSelector(
    (state: iRootState) => state.availableBrokersAndEstates.estates || []
  );
  const [imports, setImports] = useState<YelumBatchImport[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [estateFilter, setEstateFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const intervalRef = useRef<number | null>(null);

  const loadImports = useCallback(async (showLoader = false) => {
    if (showLoader) setRefreshing(true);
    try {
      setImports(await listYelumBatchImports(estateFilter || undefined, statusFilter || undefined));
    } catch (error) {
      dangerNotification(
        'Erro',
        (error as Error)?.message || 'Não foi possível carregar o histórico de importações.'
      );
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [estateFilter, statusFilter]);

  useEffect(() => {
    loadImports(true);
  }, [loadImports]);

  useEffect(() => {
    if (intervalRef.current) {
      window.clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    if (imports.some(item => isPollingStatus(item.status))) {
      intervalRef.current = window.setInterval(() => loadImports(), 10000);
    }

    return () => {
      if (intervalRef.current) window.clearInterval(intervalRef.current);
    };
  }, [imports, loadImports]);

  const handleDownload = async (item: YelumBatchImport) => {
    setDownloadingId(item.id);
    try {
      await downloadYelumBatchImportReport(item.id);
    } catch (error) {
      dangerNotification(
        'Erro',
        (error as Error)?.message || 'Não foi possível baixar o relatório.'
      );
    } finally {
      setDownloadingId(null);
    }
  };

  if (loading) {
    return (
      <PageContainer>
        <Loader active inline="centered" size="large">
          Carregando histórico...
        </Loader>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Header>
        <h1>
          <Icon name="history" /> Importações de Lote Yelum
        </h1>
        <HeaderActions>
          <Button basic color="blue" loading={refreshing} onClick={() => loadImports(true)}>
            <Icon name="refresh" /> Atualizar
          </Button>
          <Button color="purple" onClick={() => setModalOpen(true)}>
            <Icon name="plus" /> Nova importação
          </Button>
        </HeaderActions>
      </Header>

      <Grid>
        <Grid.Column width={16}>
          <TableContainer>
            <Form>
              <Form.Group widths="equal">
                <Form.Select
                  fluid
                  search
                  clearable
                  label="Imobiliária"
                  placeholder="Todas as imobiliárias"
                  options={estateOptions.map(estate => ({
                    key: estate.key,
                    text: estate.text,
                    value: String(estate.value?.id || estate.value)
                  }))}
                  value={estateFilter}
                  onChange={(_, data) => setEstateFilter(String(data.value || ''))}
                />
                <Form.Select
                  fluid
                  clearable
                  label="Status"
                  placeholder="Todos os status"
                  options={Object.entries(statusLabels).map(([value, option]) => ({
                    key: value,
                    value,
                    text: option.text
                  }))}
                  value={statusFilter}
                  onChange={(_, data) => setStatusFilter(String(data.value || ''))}
                />
              </Form.Group>
            </Form>
            <Table celled striped>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>Data e hora</Table.HeaderCell>
                  <Table.HeaderCell>Imobiliária</Table.HeaderCell>
                  <Table.HeaderCell>Arquivo</Table.HeaderCell>
                  <Table.HeaderCell>Status</Table.HeaderCell>
                  <Table.HeaderCell>Progresso</Table.HeaderCell>
                  <Table.HeaderCell textAlign="center">Ações</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {imports.length === 0 ? (
                  <Table.Row>
                    <Table.Cell colSpan={6} textAlign="center">
                      <Icon name="inbox" size="huge" color="grey" />
                      <p>Nenhuma importação encontrada.</p>
                    </Table.Cell>
                  </Table.Row>
                ) : (
                  imports.map(item => {
                    const progress = Math.min(100, Math.max(0, getProgress(item)));
                    const status = getStatusLabel(item.status);
                    const canDownload = [
                      'SUCCESS',
                      'COMPLETED',
                      'COMPLETED_WITH_ERRORS',
                      'FAILED'
                    ].includes(item.status);
                    return (
                      <Table.Row key={item.id}>
                        <Table.Cell>
                          {item.createdAt
                            ? moment(item.createdAt).format('DD/MM/YYYY HH:mm')
                            : '-'}
                        </Table.Cell>
                        <Table.Cell>
                          {item.estate?.name ||
                            estateOptions.find(
                              estate => String(estate.value?.id || estate.value) === item.estateId
                            )?.text ||
                            item.estateId ||
                            '-'}
                        </Table.Cell>
                        <Table.Cell>
                          {item.fileName || item.filename || item.originalFileName || '-'}
                        </Table.Cell>
                        <Table.Cell>
                          <Label color={status.color as any}>{status.text}</Label>
                        </Table.Cell>
                        <Table.Cell>
                          <Progress
                            percent={progress}
                            size="small"
                            color={status.color as any}
                            active={isPollingStatus(item.status)}
                          >
                            {item.processedItems != null && item.totalItems != null
                              ? `${item.processedItems}/${item.totalItems}`
                              : `${Math.round(progress)}%`}
                          </Progress>
                        </Table.Cell>
                        <Table.Cell textAlign="center">
                          <Button
                            basic
                            size="small"
                            color="blue"
                            title="Abrir importação"
                            onClick={() =>
                              history.push(`/admin/fire-insurance-batches/yelum-imports/${item.id}`)
                            }
                          >
                            <Icon name="folder open" /> Abrir
                          </Button>
                          {canDownload && (
                            <Button
                              basic
                              size="small"
                              color="green"
                              title="Baixar relatório"
                              loading={downloadingId === item.id}
                              disabled={downloadingId !== null}
                              onClick={() => handleDownload(item)}
                            >
                              <Icon name="download" />
                            </Button>
                          )}
                        </Table.Cell>
                      </Table.Row>
                    );
                  })
                )}
              </Table.Body>
            </Table>
          </TableContainer>
        </Grid.Column>
      </Grid>

      <NewYelumImportModal open={modalOpen} onClose={() => setModalOpen(false)} />
    </PageContainer>
  );
};

export default YelumImportsPage;
